import React, { useState, FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRouter } from 'next/router';
import Name from '@components/join/basicInfo/name';
import BirthDate from '@components/join/basicInfo/birthDate';
import Mobile from '@components/join/basicInfo/mobile';
import UserId from '@components/join/basicInfo/userId';
import Email from '@components/join/basicInfo/email';
import Password from '@components/join/basicInfo/password';
import RePassword from '@components/join/basicInfo/repassword';
import styles from '@components/join/join.module.scss';
import Agree from '@components/join/agree';
import joinAlert from '@components/join/joinAlert';
import { useSaveJoinContent } from '@repository/register/useRegister';
import { useLogin, useUserInfo } from '@repository/auth/useUserInfo';
import JoinRq, { JoinData } from '@domain/rq/register/JoinRq';
import KcbSmsVerificationRs from '@domain/rs/verification/KcbSmsVerificationRs';
import KcbIpinVerificationRs from '@domain/rs/verification/KcbIpinVerificationRs';
import { MobileCertStateType } from '@domain/constant/MobileCertStateType';
import { JobflexQuery } from '@domain/constant/JobflexQueryType';
import { V2Route, V2RouteUrl } from '@utils/routeUtil';
import useDebounce from '@utils/hooks/useDebounce';
import { ERROR_TEXT } from '@utils/validators/text';
import classnames from 'classnames/bind';
const cx = classnames.bind(styles);

interface BaseInfoProps {
  verificationResponse: KcbSmsVerificationRs | KcbIpinVerificationRs | null;
}

const BaseInfo: FC<BaseInfoProps> = ({ verificationResponse }: BaseInfoProps) => {
  const router = useRouter();
  // id 중복 체크 상태 값
  const [isRedirect, setIsRedirect] = useState(true);
  const [idState, setIdState] = useState<'Duplication' | 'Checked' | null>(null); // Duplication: 아이디 중복, Checked: 아이디 중복 확인 완료, null: 중복 확인 안함
  // Mobile 인증 진행 상태 값
  const [mobileCertState, setMobileCertState] = useState<MobileCertStateType | null>(null);
  // JF3, Builder 에서 넘어온 회원가입인 경우
  const isRedirectType =
    router.query?.type === JobflexQuery.resume ||
    router.query?.type === JobflexQuery.mypage ||
    router.query?.type === 'builder';
  const { mutateAsync: fetchSaveJoin } = useSaveJoinContent();
  const { mutateAsync: fetchLogin } = useLogin(false);
  const { data: userInfo } = useUserInfo();

  const {
    handleSubmit,
    setError,
    trigger,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext();
  const userId = watch('userId');
  const mobile = watch('mobile');
  const token = watch('token');

  // 회원 가입 요청 Debounce 처리
  const onClickSubmitBtn = useDebounce(async () => {
    const isSuccess = await trigger();

    if (userId && idState !== 'Checked') {
      if (!errors.userId?.message) setError('userId', { message: ERROR_TEXT.ID_DUPLICATE_REQUIRED });
      if (errors.userId?.message) setError('userId', { message: errors.userId?.message as string });
    }

    if (mobile && !token && mobileCertState !== 'SUCCESS' && mobileCertState !== 'ING') {
      if (!errors.mobile?.message) setError('mobile', { message: ERROR_TEXT.MOBILE_CERT_REQUIRED });
      if (errors.mobile?.message) setError('mobile', { message: errors.mobile?.message as string });
    }

    if (token && mobileCertState !== 'SUCCESS') {
      if (!errors.token?.message) setError('token', { message: ERROR_TEXT.MOBILE_CERT_REQUIRED });
      if (errors.token?.message) setError('token', { message: errors.token?.message as string });
    }

    if (isSuccess && idState === 'Checked' && mobileCertState === 'SUCCESS') {
      handleSubmit((e: any) => onSubmit(e))();
    }
  }, 500);

  const onSubmit = async (data: JoinData) => {
    try {
      //disabled 데이터는 submit시 데이터 못가져오기 떄문에 getvalue로 따로 조회해야함
      const token = getValues('token');
      const identificationToken = verificationResponse?.identificationToken ?? '';

      await fetchSaveJoin(
        new JoinRq({
          ...router.query,
          ...data,
          token,
          identificationToken,
        }),
      );

      //회원 가입 완료후 로그인
      await fetchLogin({
        id: data.userId,
        password: data.password,
        autoLogin: false,
      });

      setIsRedirect(false);
      router.push({
        pathname: V2RouteUrl.WELCOME,
        query: isRedirectType ? { redirectUrl: sessionStorage.getItem('prevPath') } : null,
      });
    } catch (error: any) {
      joinAlert(setError, error);
    }
  };

  useEffect(() => {
    //로그인 상태에서 회원가입 접근안되도록 함
    if (userInfo && isRedirect) {
      V2Route.goMainPage();
    }
  }, [userInfo]);

  return (
    <>
      <div className={cx('joinWrap')}>
        {/* 이름 */}
        <Name prefixName={'name'} />

        {/* 생년월일 */}
        <BirthDate prefixName={'birthDate'} />

        {/* 휴대폰*/}
        <Mobile prefixName={'mobile'} mobileCertState={mobileCertState} setMobileCertState={setMobileCertState} />

        {/* 이메일 */}
        <Email prefixName={'email'} />

        <div className={cx('divider')} />

        {/* 아이디 */}
        <UserId prefixName={'userId'} idState={idState} setIdState={setIdState} />

        {/* 비밀번호 */}
        <Password prefixName={'password'} />

        {/* 비밀번호 확인 */}
        <RePassword prefixName={'rePassword'} />

        {/* 동의문 영역 */}
        <Agree />
      </div>

      <button type="button" className={cx('btnNext')} onClick={onClickSubmitBtn}>
        가입 완료
      </button>

      {/* <WelcomeModal visible={modalVisible} /> */}
    </>
  );
};

export default BaseInfo;
